import React, { useState } from 'react';
import axios from 'axios';
import CustomIcon from '../Common/CustomIcon/CustomIcon.js';

import {
	getSession,
} from 'Vulture/Session.js';

import {
	broncoURL,
} from 'Vulture/ENV.js';

import style from './Migration.module.css';

// Sentinel for the picker's "NO DESIGN" choice: migrate the property's data
// without creating a template / cloning a design skeleton (no design
// alignment or listing assignment). Never sent as design_name — handleMigrate
// translates it to no_design: true. (DEV-11982)
const NO_DESIGN = '__NO_DESIGN__';

function MigrationView({ app, accountUUID }) {
	const [propertyNumber, setPropertyNumber] = useState('');
	const [loading, setLoading] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [stamping, setStamping] = useState(false);
	const [stampingSystem, setStampingSystem] = useState(null);
	const [preview, setPreview] = useState(null);
	const [result, setResult] = useState(null);
	const [error, setError] = useState(null);
	const [migratedPropertyName, setMigratedPropertyName] = useState(null);
	// Result of a failed connectivity preflight: { message, details } where
	// details is an optional technical detail line; null hides the modal.
	const [connectivityError, setConnectivityError] = useState(null);
	// DEV-11880: the committed Spark Design choice (auto-match from the preview,
	// or the user's pick). Migration is blocked until this is set.
	const [selectedDesign, setSelectedDesign] = useState(null);
	// DEV-11907: design per ALTERNATE configuration group, keyed by the group's
	// first directory number (string). Every group needs a design to migrate.
	const [altDesigns, setAltDesigns] = useState({});
	// Which design the picker modal edits: 'main' or an alternate group key.
	const [designModalTarget, setDesignModalTarget] = useState('main');
	// DEV-11907: per-system template choice for the stamping step, keyed by
	// system uuid (values are template uuids from account_templates).
	const [systemDesigns, setSystemDesigns] = useState({});
	// DEV-11907: which directories' wayfinding maps/paths to import (seeded
	// with every directory that has wayfinding).
	const [wayfindingDirs, setWayfindingDirs] = useState([]);
	// DEV-12124: path-network consolidation distances, % of the map diagonal.
	// Blank sends nothing and bronco applies its defaults (1 / 3).
	const [wfMergePct, setWfMergePct] = useState('');
	const [wfReconnectPct, setWfReconnectPct] = useState('');
	const [wfLocationSnapPx, setWfLocationSnapPx] = useState('');

	// Formats "2160x3840" as "2160 × 3840 (4K Vertical)" per the mockups.
	const formatResolution = (res) => {
		const parts = String(res || '').toLowerCase().split('x');
		const w = parseInt(parts[0], 10);
		const h = parseInt(parts[1], 10);
		if (!w || !h) {
			return res || '1920 × 1080 (HD Horizontal)';
		}
		const quality = Math.max(w, h) >= 3840 ? '4K' : Math.max(w, h) >= 1920 ? 'HD' : '';
		const orientation = h > w ? 'Vertical' : 'Horizontal';
		return `${w} × ${h}${quality ? ` (${quality} ${orientation})` : ` (${orientation})`}`;
	};
	// Design list from the Migration Design Portfolio (null = not loaded yet,
	// [] = portfolio empty); designsError holds a non-breaking load-failure note.
	const [designs, setDesigns] = useState(null);
	const [designsError, setDesignsError] = useState(null);
	const [designModalOpen, setDesignModalOpen] = useState(false);
	// Dropdown value while the modal is open; committed on "Change Design".
	const [modalDesign, setModalDesign] = useState('');
	// DEV-11880: inline design preview inside the picker modal. Staged via
	// GET /migration/design/preview (the same temp-store mechanism the Step 1
	// template preview uses). {name, previews: [{key, orientation, width,
	// height}]} when shown — orientation-agnostic designs (no Hz/Vt in the
	// name) stage both orientations, shown side by side.
	const [designPreview, setDesignPreview] = useState(null);
	const [designPreviewLoading, setDesignPreviewLoading] = useState(false);
	const [designPreviewError, setDesignPreviewError] = useState(null);

	const accountName = app?.state?.accountName || '';

	// DEV-11880: the design list is generated from the portfolio account at
	// request time so newly added designs appear without a code change.
	const fetchDesigns = async () => {
		const session = getSession();
		try {
			const response = await axios.get(`${broncoURL}/migration/designs`, {
				headers: { 'X-Auth-Session': session.session },
			});
			setDesigns(response.data?.designs || []);
			setDesignsError(null);
		} catch (err) {
			console.error('Design list error:', err);
			setDesigns(null);
			setDesignsError('The design list could not be loaded. Close this dialog and try again.');
		}
	};

	// DEV-11912 wayfinding multi-select dropdown: open state + outside-click
	// close via a wrapper ref.
	const [wayfindingMenuOpen, setWayfindingMenuOpen] = useState(false);
	const wayfindingMenuRef = React.useRef(null);
	React.useEffect(() => {
		if (!wayfindingMenuOpen) {
			return undefined;
		}
		const onDown = (e) => {
			if (wayfindingMenuRef.current && !wayfindingMenuRef.current.contains(e.target)) {
				setWayfindingMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
	}, [wayfindingMenuOpen]);

	// Tracks the design name of the in-flight preview request so a response
	// that arrives after the user changed the selection is discarded instead of
	// repainting the previous design's preview under the new selection.
	const previewRequestRef = React.useRef(null);

	const clearDesignPreview = () => {
		previewRequestRef.current = null; // invalidate any in-flight request
		setDesignPreview(null);
		setDesignPreviewLoading(false);
		setDesignPreviewError(null);
	};

	// Eye toggle: stage a preview of the design selected in the dropdown, or
	// hide the panel if it's already showing that design. NO DESIGN has nothing
	// to preview. (DEV-11982)
	const handleTogglePreview = async () => {
		if (!modalDesign || modalDesign === NO_DESIGN) {
			return;
		}
		if (designPreview?.name === modalDesign) {
			clearDesignPreview();
			return;
		}
		const requestedDesign = modalDesign;
		previewRequestRef.current = requestedDesign;
		setDesignPreview(null);
		setDesignPreviewError(null);
		setDesignPreviewLoading(true);

		const session = getSession();
		try {
			const response = await axios.get(`${broncoURL}/migration/design/preview`, {
				params: { design_name: requestedDesign },
				headers: { 'X-Auth-Session': session.session },
			});
			if (previewRequestRef.current !== requestedDesign) {
				return; // Selection changed while loading — stale response.
			}
			const previews = response.data?.previews || [];
			if (previews.length === 0) {
				setDesignPreviewError('The preview could not be loaded.');
				return;
			}
			setDesignPreview({
				name: requestedDesign,
				previews,
			});
		} catch (err) {
			if (previewRequestRef.current !== requestedDesign) {
				return;
			}
			console.error('Design preview error:', err);
			setDesignPreviewError(err.response?.data?.message || 'The preview could not be loaded.');
		} finally {
			if (previewRequestRef.current === requestedDesign) {
				setDesignPreviewLoading(false);
			}
		}
	};

	const handleVerify = async () => {
		if (!propertyNumber) {
			setError('Property Number is required');
			return;
		}

		setVerifying(true);
		setError(null);
		setPreview(null);
		setResult(null);
		setConnectivityError(null);
		setSelectedDesign(null);
		setDesignModalOpen(false);
		setWayfindingMenuOpen(false);

		const session = getSession();
		const authHeaders = { 'X-Auth-Session': session.session };

		try {
			// Preflight: probe upstream servers (Pinto, Updates) end-to-end
			// for this property before kicking off the verify, so any
			// outage / DB issue / pipeline error surfaces as a clear modal
			// instead of a confusing late-stage failure.
			const ping = await axios.get(`${broncoURL}/migration/connectivity`, {
				params: { property_number: parseInt(propertyNumber, 10) },
				headers: authHeaders,
			});
			if (!ping.data.ok) {
				console.error('Migration connectivity preflight failed:', ping.data);
				if (ping.data.no_pc_records) {
					// Property exists but has no migratable PC lines (e.g.
					// decommissioned) — a data-state problem, not an outage.
					setConnectivityError({
						message: 'No PC Records for this property number.',
					});
					return;
				}
				const names = [];
				const details = [];
				if (!ping.data.pinto?.ok) {
					names.push('Pinto');
					if (ping.data.pinto?.error) {
						details.push(`Pinto: ${ping.data.pinto.error}`);
					}
				}
				if (!ping.data.updates?.ok) {
					names.push('Updates');
					if (ping.data.updates?.error) {
						details.push(`Updates: ${ping.data.updates.error}`);
					}
				}
				setConnectivityError({
					message: `The ${names.join(' and ')} server${names.length > 1 ? 's' : ''} did not respond as expected for this property. Please contact the Development team for assistance.`,
					details: details.join(' — '),
				});
				return;
			}

			const response = await axios.get(
				`${broncoURL}/migration/property/preview`,
				{
					params: {
						property_number: parseInt(propertyNumber, 10),
						account_uuid: accountUUID,
					},
					headers: authHeaders,
				}
			);

			setPreview(response.data);
			// DEV-11880: seed the committed design from the auto-match (empty =
			// no match → the user must pick one) and load the portfolio list.
			setSelectedDesign(response.data?.spark_design || null);
			// DEV-11907: seed alternate-group designs from their auto-matches
			// and the per-system stamping choices from the stored assignments.
			const seededAlts = {};
			(response.data?.config_groups || []).forEach((g) => {
				if (!g.is_main && g.directories?.length) {
					seededAlts[String(g.directories[0])] = g.spark_design || null;
				}
			});
			setAltDesigns(seededAlts);
			const seededSystems = {};
			(response.data?.systems || []).forEach((s) => {
				if (s.uuid && s.assigned_template_uuid) {
					seededSystems[s.uuid] = s.assigned_template_uuid;
				}
			});
			setSystemDesigns(seededSystems);
			// DEV-11912 default rule: pre-select Directory 1's wayfinding when
			// present, else the lowest directory that has any. The list arrives
			// sorted ascending, so [0] is the lowest. Others are opt-in.
			const wfAvailable = response.data?.wayfinding_directories || [];
			setWayfindingDirs(wfAvailable.includes(1) ? [1] : wfAvailable.length ? [wfAvailable[0]] : []);
			await fetchDesigns();
		} catch (err) {
			console.error('Preview error:', err);
			if (err.response?.data?.message) {
				setError(err.response.data.message);
			} else {
				setError(err.message || 'Failed to verify property');
			}
		} finally {
			setVerifying(false);
		}
	};

	const handleMigrate = async () => {
		if (!propertyNumber) {
			setError('Property Number is required');
			return;
		}
		if (!accountUUID) {
			setError('No account selected. Please select an account first.');
			return;
		}
		// DEV-11880: a design is required — no auto-match means the user must
		// choose one before migrating.
		if (!selectedDesign) {
			setError('Choose a Spark Design before migrating.');
			return;
		}
		// DEV-11907: every alternate configuration also needs a choice — a real
		// design or NO DESIGN (the sentinel counts as a choice; each group
		// decides independently).
		const missingAlt = (preview?.config_groups || []).some((g) =>
			!g.is_main && g.directories?.length && !altDesigns[String(g.directories[0])]);
		if (missingAlt) {
			setError('Choose a Spark Design (or No Design) for every configuration before migrating.');
			return;
		}

		setLoading(true);
		setError(null);
		setResult(null);

		const session = getSession();

		// NO DESIGN is a client-side sentinel and a PER-CONFIGURATION choice:
		// main → no_design flag; alternates → no_design_directories. Never
		// sent as a design name. (DEV-11982)
		const migrateBody = {
			property_number: parseInt(propertyNumber, 10),
			account_uuid: accountUUID,
			wayfinding_directories: wayfindingDirs,
		};
		if (wayfindingDirs.length > 0) {
			// Blank/0 sends nothing (bronco default); any other number, negatives
			// included, is sent so bronco clamps it and returns a notice.
			const tuning = {
				wayfinding_merge_pct: wfMergePct,
				wayfinding_reconnect_pct: wfReconnectPct,
				wayfinding_location_snap_px: wfLocationSnapPx,
			};
			Object.entries(tuning).forEach(([field, raw]) => {
				const value = parseFloat(raw);
				if (Number.isFinite(value) && value !== 0) migrateBody[field] = value;
			});
		}
		if (selectedDesign === NO_DESIGN) {
			migrateBody.no_design = true;
		} else {
			migrateBody.design_name = selectedDesign;
		}
		const directoryDesigns = {};
		const noDesignDirectories = [];
		(preview?.config_groups || []).forEach((g) => {
			if (g.is_main || !g.directories?.length) {
				return;
			}
			const chosen = altDesigns[String(g.directories[0])];
			if (chosen === NO_DESIGN) {
				noDesignDirectories.push(...g.directories);
			} else if (chosen) {
				g.directories.forEach((d) => {
					directoryDesigns[String(d)] = chosen;
				});
			}
		});
		migrateBody.directory_designs = directoryDesigns;
		migrateBody.no_design_directories = noDesignDirectories;

		try {
			const response = await axios.post(
				`${broncoURL}/migration/property`,
				migrateBody,
				{
					headers: {
						'X-Auth-Session': session.session,
					},
				}
			);

			setResult(response.data);
			setMigratedPropertyName(preview?.property_name || null); // Save property name before clearing
			setPreview(null); // Clear preview after successful migration

			// Refresh account hierarchy so the new property/systems appear
			app.fetchAccount(accountUUID);
			// The ListingConfigProvider only refetches on account change — a
			// migration mints NEW listing configs under the SAME account, so
			// poke it or every consumer (Programming listing groups, Content's
			// ListingsModal) renders from a stale map until a full reload —
			// ListingsModal hard-crashed on the missing config. (DEV-12016)
			app.listingConfigRef?.current?.fetchListingConfigs()?.catch?.((e) => console.error('Listing config refetch after migration failed:', e));
		} catch (err) {
			console.error('Migration error:', err);
			if (err.response?.data?.message) {
				setError(err.response.data.message);
			} else {
				setError(err.message || 'Migration failed');
			}
		} finally {
			setLoading(false);
		}
	};

	// Resolves the stamping selection for a system: the user's per-system
	// choice ('' = explicit "No Design"), else the stored migration
	// assignment. Empty means the system cannot stamp until the admin picks a
	// real design from its dropdown. (DEV-11982 per-configuration NO DESIGN)
	const systemStampSelection = (system) => {
		if (systemDesigns[system.uuid] !== undefined) {
			return systemDesigns[system.uuid];
		}
		return system.assigned_template_uuid || '';
	};

	const handleStampSystems = async () => {
		if (!preview?.systems || !accountUUID) {
			setError('Missing required data for stamping');
			return;
		}

		// DEV-11742: skipped systems were never migrated, so they can't be
		// stamped. Systems on "No Design" (no selection) are left alone — they
		// stay Not Stamped until a real design is picked for them. (DEV-11982)
		const stampableSystems = preview.systems.filter(s =>
			!s.is_stamped && !s.is_skipped && systemStampSelection(s));
		if (stampableSystems.length === 0) {
			setError('Select a design for at least one system to stamp.');
			return;
		}

		setStamping(true);
		setError(null);

		const session = getSession();

		try {
			// Stamp each system one by one, each with ITS design — the user's
			// per-system choice, else the stored migration assignment. (DEV-11907)
			for (const system of stampableSystems) {
				// Key by uuid — "{prop}-{dir}" display names can collide when a
				// directory has more than one PC. (DEV-11907)
				setStampingSystem(system.uuid);

				const templateUUID = systemStampSelection(system);
				const assignmentMap = {};
				assignmentMap[`system.${system.uuid}.template.${templateUUID}`] = true;

				await axios.post(
					`${broncoURL}/system/template_assignment`,
					{
						account_uuid: accountUUID,
						assignment_map: assignmentMap,
					},
					{
						headers: {
							'X-Auth-Session': session.session,
						},
					}
				);

				// Update local state to show system as stamped — with the design
				// it was ACTUALLY stamped with, which may differ from the stored
				// migration assignment. (gatekeep E5)
				const stampedName = (preview.account_templates || []).find((t) => t.uuid === templateUUID)?.display_name;
				setPreview(prev => ({
					...prev,
					systems: prev.systems.map(s =>
						s.uuid === system.uuid
							? { ...s, is_stamped: true, assigned_design: stampedName || s.assigned_design, assigned_template_uuid: templateUUID }
							: s
					),
				}));
			}

			setStampingSystem(null);

			// Refresh account hierarchy so stamped systems/previews appear
			app.fetchAccount(accountUUID);
			app.listingConfigRef?.current?.fetchListingConfigs()?.catch?.((e) => console.error('Listing config refetch after stamping failed:', e)); // see migrate handler (DEV-12016)
		} catch (err) {
			console.error('Stamping error:', err);
			if (err.response?.data?.message) {
				setError(err.response.data.message);
			} else {
				setError(err.message || 'Failed to stamp systems');
			}
		} finally {
			setStamping(false);
			setStampingSystem(null);
		}
	};

	const handleReset = () => {
		setPropertyNumber('');
		setPreview(null);
		setResult(null);
		setError(null);
		setMigratedPropertyName(null);
		setSelectedDesign(null);
		setAltDesigns({});
		setSystemDesigns({});
		setWayfindingDirs([]);
		setWayfindingMenuOpen(false);
		setWfMergePct('');
		setWfReconnectPct('');
		setWfLocationSnapPx('');
		setDesigns(null);
		setDesignsError(null);
		setDesignModalOpen(false);
		clearDesignPreview();
	};

	// DEV-11880/11907: design-picker modal handlers. `target` is 'main' or an
	// alternate group key (the group's first directory, as a string). Cancel
	// leaves the committed selection untouched; "Change Design" commits.
	const openDesignModal = (target = 'main') => {
		setDesignModalTarget(target);
		setModalDesign((target === 'main' ? selectedDesign : altDesigns[target]) || '');
		setDesignModalOpen(true);
		clearDesignPreview();
		// Retry a failed/never-run list load so a transient failure recovers
		// without re-verifying.
		if (designs === null) {
			fetchDesigns();
		}
	};

	const closeDesignModal = () => {
		setDesignModalOpen(false);
		clearDesignPreview();
	};

	const confirmDesign = () => {
		if (modalDesign) {
			if (designModalTarget === 'main') {
				setSelectedDesign(modalDesign);
			} else {
				setAltDesigns((prev) => ({ ...prev, [designModalTarget]: modalDesign }));
			}
		}
		closeDesignModal();
	};

	// DEV-11742: skipped systems aren't part of the stamp lifecycle.
	const unstampedCount = preview?.systems?.filter(s => !s.is_stamped && !s.is_skipped).length || 0;
	const stampedCount = preview?.systems?.filter(s => s.is_stamped && !s.is_skipped).length || 0;
	const skippedCount = preview?.systems?.filter(s => s.is_skipped).length || 0;

	return (
		<div className={style.migrationContainer}>
			<div className={style.migrationCard}>
				<h1 className={style.title}>Property Migration</h1>
				<p className={style.subtitle}>Migrate properties from Atlas/4.5 to Spark</p>

				<div className={style.form}>
					<div className={style.formGroup}>
						<label className={style.label}>Target Account</label>
						<div className={style.accountDisplay}>
							{accountName ? (
								<span className={style.accountName}>{accountName}</span>
							) : (
								<span className={style.accountWarning}>No account selected</span>
							)}
						</div>
						{accountUUID && (
							<div className={style.accountUUID}>{accountUUID}</div>
						)}
					</div>

					<div className={style.formGroup}>
						<label className={style.label}>Property Number</label>
						<div className={style.inputGroup}>
							<input
								type="number"
								className={style.input}
								data-test-id="migrationPropertyNumberInput"
								value={propertyNumber}
								onChange={(e) => {
									setPropertyNumber(e.target.value);
									setPreview(null);
									setResult(null);
									setError(null);
								}}
								placeholder="e.g., 30251"
								disabled={loading || verifying || stamping}
							/>
							<button
								className={style.verifyButton}
								data-test-id="migrationVerifyButton"
								onClick={handleVerify}
								disabled={loading || verifying || stamping || !propertyNumber}
							>
								{/* On the migration-success screen the same action moves the
								    user into the stamping phase — label it accordingly. */}
								{verifying ? 'Verifying...' : (result?.success ? 'Prepare to Stamp' : 'Verify Property')}
							</button>
						</div>
					</div>

					{preview && preview.success && !preview.already_migrated && (preview.config_groups || []).length > 0 && (
						<div className={style.formGroup}>
							<label className={style.label}>Systems</label>
							<div className={style.systemsBanner}>
								<span className={style.systemsBannerCheck}>✓</span>
								<span>Unique Designs Detected:</span>
								{(preview.config_groups || []).map((g) => (
									g.directories?.length ? (
										<span key={g.directories[0]} className={style.systemChip}>
											System{g.directories[0]}
										</span>
									) : null
								))}
							</div>
							<div className={style.systemsBannerNote}>
								Systems are now resolved automatically by scanning each system program — no manual selection needed.
							</div>
						</div>
					)}

					{/* Wayfinding multi-select per the DEV-11912 mockup: chips in a
				    select-style field; dropdown lists ONLY directories whose legacy
				    paths.xml exists (preview.wayfinding_directories) — offering a
				    directory with no wayfinding data made the import silently skip
				    it (P1993 D3/D5). Shown whenever the property has ANY wayfinding
				    (single-dir properties can opt out too); hidden only when there
				    is none. */}
					{preview && preview.success && !preview.already_migrated && (preview.wayfinding_directories || []).length > 0 && (
						<div className={style.formGroup} ref={wayfindingMenuRef}>
							<label className={style.label}>Wayfinding Maps and Paths</label>
							<div className={style.wfSelect} onClick={() => setWayfindingMenuOpen((o) => !o)}>
								<div className={style.wfSelectChips}>
									{wayfindingDirs.map((d) => (
										<span key={d} className={style.wfChip}>
											System {d}
											<button
												className={style.wfChipRemove}
												onClick={(e) => {
													e.stopPropagation();
													setWayfindingDirs(wayfindingDirs.filter(x => x !== d));
												}}
												disabled={loading || verifying}
												aria-label={`Remove System ${d} wayfinding`}
											>
												×
											</button>
										</span>
									))}
									{wayfindingDirs.length === 0 && (
										<span className={style.wfPlaceholder}>No wayfinding selected</span>
									)}
								</div>
								<span className={style.wfCaret}>▾</span>
							</div>
							{wayfindingMenuOpen && (
								<div className={style.wfMenu}>
									<div className={style.wfMenuHeader}>
										<span>{wayfindingDirs.length} of {(preview.wayfinding_directories || []).length} selected</span>
										<span className={style.wfMenuActions}>
											<button
												className={style.wfSelectAll}
												onClick={() => setWayfindingDirs([...(preview.wayfinding_directories || [])])}
												disabled={loading || verifying}
											>
												Select all
											</button>
											<button
												className={style.wfClear}
												onClick={() => setWayfindingDirs([])}
												disabled={loading || verifying}
											>
												Clear
											</button>
										</span>
									</div>
									<div className={style.wfOptionGrid}>
										{(preview.wayfinding_directories || []).map((d) => (
											<label key={d} className={style.wfOption}>
												<input
													type="checkbox"
													checked={wayfindingDirs.includes(d)}
													onChange={(e) => setWayfindingDirs(e.target.checked
														? [...wayfindingDirs, d].sort((a, b) => a - b)
														: wayfindingDirs.filter(x => x !== d))}
													disabled={loading || verifying}
												/>
												Directory {d}
											</label>
										))}
									</div>
								</div>
							)}
							{/* DEV-12124: tunable path-network consolidation distances. */}
							{wayfindingDirs.length > 0 && (
								<div className={style.wfTuning}>
									<label className={style.wfTuningField}>
										<span>Merge distance (% of map)</span>
										<input
											type="number"
											className={style.input}
											min="0.1"
											max="20"
											step="0.1"
											placeholder="1"
											value={wfMergePct}
											onChange={(e) => setWfMergePct(e.target.value)}
											disabled={loading || verifying}
											title="Corridor path points closer together than this become one point, and corridor points this close to a path line are joined onto it. Raise it for roughly drawn legacy paths. Never combines suite locations."
											data-test-id="migration-wf-merge-pct"
										/>
									</label>
									<label className={style.wfTuningField}>
										<span>Reconnect distance (% of map)</span>
										<input
											type="number"
											className={style.input}
											min="0.1"
											max="50"
											step="0.1"
											placeholder="3"
											value={wfReconnectPct}
											onChange={(e) => setWfReconnectPct(e.target.value)}
											disabled={loading || verifying}
											title="How far an isolated stretch of path may be from the rest of the network and still be joined to it quietly. Isolated pieces that hold a suite location are always joined (and noted in the import notes); bare stray path pieces further away than this are dropped."
											data-test-id="migration-wf-reconnect-pct"
										/>
									</label>
									<label className={style.wfTuningField}>
										<span>Location snap distance (px)</span>
										<input
											type="number"
											className={style.input}
											min="1"
											max="500"
											step="1"
											placeholder="10"
											value={wfLocationSnapPx}
											onChange={(e) => setWfLocationSnapPx(e.target.value)}
											disabled={loading || verifying}
											title="A corridor turn point this close (pixels) to a suite location is folded into the location, which keeps its own position. Junctions and the start point are not folded unless a location sits exactly on them. Locations are never combined with each other or moved onto a line. Out-of-range values are clamped and noted in the import notes."
											data-test-id="migration-wf-location-snap-px"
										/>
									</label>
								</div>
							)}
						</div>
					)}

					{preview && preview.success && (
						<div className={style.preview} data-test-id="migrationPreview">
							<h3 className={style.previewTitle}>
								{!preview.already_migrated && preview.config_groups?.[0]?.directories?.length
									? `System ${preview.config_groups[0].directories[0]} Property Details`
									: 'Property Details'}
							</h3>
							<div className={style.previewDetails}>
								<p><strong>Property Name:</strong> {preview.property_name}</p>
								<p><strong>Property Number:</strong> {preview.property_number}</p>
								<p><strong>Template:</strong> {preview.template || 'N/A'}</p>
								<p><strong>Resolution:</strong> {formatResolution(preview.config_groups?.[0]?.resolution || preview.resolution)}</p>
								<p><strong>Static:</strong> {preview.static === '1' ? 'Yes' : 'No'}</p>
								{!preview.already_migrated && (
									<>
										{!preview.spark_design && (
											<div className={style.designNoMatchNote}>
												We do not have a match for this project. Please choose the design you want.
											</div>
										)}
										<p className={style.designRow}>
											<strong>Spark Design:</strong>{' '}
											{selectedDesign === NO_DESIGN
												? <span className={style.designNoDesign}>No Design (data import only)</span>
												: selectedDesign
													? <span>{selectedDesign}</span>
													: <span className={style.designMissing}>None selected</span>}
											<button
												className={style.designChangeButton}
												onClick={() => openDesignModal('main')}
												disabled={loading || verifying || stamping}
											>
												Change
											</button>
										</p>
									</>
								)}
								<div className={style.previewCounts}>
									<div className={style.countBox} data-test-id="migrationCountBox-systems">
										<span className={style.countNumber} data-test-id="migrationCountNumber-systems">{preview.systems_count}</span>
										<span className={style.countLabel}>Systems</span>
									</div>
									<div className={style.countBox} data-test-id="migrationCountBox-companies">
										<span className={style.countNumber} data-test-id="migrationCountNumber-companies">{preview.companies_count}</span>
										<span className={style.countLabel}>Companies</span>
									</div>
									<div className={style.countBox} data-test-id="migrationCountBox-individuals">
										<span className={style.countNumber} data-test-id="migrationCountNumber-individuals">{preview.individuals_count}</span>
										<span className={style.countLabel}>Individuals</span>
									</div>
									<div className={style.countBox} data-test-id="migrationCountBox-images">
										<span className={style.countNumber} data-test-id="migrationCountNumber-images">{preview.slideshow_counts ? (preview.slideshow_counts['1'] ?? 0) : (preview.images_count ?? 0)}</span>
										<span className={style.countLabel}>Slideshow 1 Images</span>
										{preview.expired_images_count > 0 && (
											<span className={style.countSub} data-test-id="migrationExpiredNote">
												{preview.expired_images_count} expired skipped
											</span>
										)}
									</div>
									{preview.slideshow_counts?.['2'] > 0 && (
										<div className={style.countBox} data-test-id="migrationCountBox-slideshow2">
											<span className={style.countNumber} data-test-id="migrationCountNumber-slideshow2">{preview.slideshow_counts['2']}</span>
											<span className={style.countLabel}>Slideshow 2 Images</span>
										</div>
									)}
									{preview.space_count > 0 && (
										<div className={style.countBox} data-test-id="migrationCountBox-space">
											<span className={style.countNumber} data-test-id="migrationCountNumber-space">{preview.space_count}</span>
											<span className={style.countLabel}>Space Available</span>
										</div>
									)}
									{preview.amenities_count > 0 && (
										<div className={style.countBox} data-test-id="migrationCountBox-amenities">
											<span className={style.countNumber} data-test-id="migrationCountNumber-amenities">{preview.amenities_count}</span>
											<span className={style.countLabel}>Amenities</span>
										</div>
									)}
									{preview.list12_count > 0 && (
										<div className={style.countBox} data-test-id="migrationCountBox-list12">
											<span className={style.countNumber} data-test-id="migrationCountNumber-list12">{preview.list12_count}</span>
											<span className={style.countLabel}>List12</span>
										</div>
									)}
									{preview.announcements_count > 0 && (
										<div className={style.countBox} data-test-id="migrationCountBox-announcements">
											<span className={style.countNumber} data-test-id="migrationCountNumber-announcements">{preview.announcements_count}</span>
											<span className={style.countLabel}>Announcements</span>
										</div>
									)}
									{preview.has_wayfinding && (
										<div className={style.countBox} data-test-id="migrationCountBox-wayfinding">
											<span className={style.countNumber} data-test-id="migrationCountNumber-wayfinding">{preview.wayfinding_count}</span>
											<span className={style.countLabel}>
												Wayfinding{preview.wayfinding_floors > 1 ? ` · ${preview.wayfinding_floors} floors` : ''}
											</span>
										</div>
									)}
								</div>

								{preview.slideshow_warning && (
									<p className={style.warning} data-test-id="migrationSlideshowWarning">{preview.slideshow_warning}</p>
								)}
								{preview.brood_providers && preview.brood_providers.length > 0 && (
									<div className={style.providersSection}>
										<h4 className={style.providersTitle}>Brood Providers</h4>
										<div className={style.providersList}>
											{preview.brood_providers.map((provider, index) => (
												<div key={index} className={style.providerItem}>
													<span className={`${style.providerType} ${style[`provider${provider.type}`]}`}>
														{provider.type}
													</span>
													<span className={style.providerName}>{provider.display_name}</span>
													{provider.size && (
														<span className={style.providerDetail}>{provider.size}</span>
													)}
													{provider.limit > 0 && (
														<span className={style.providerDetail}>Limit: {provider.limit}</span>
													)}
												</div>
											))}
										</div>
									</div>
								)}
							</div>

							{preview.systems && preview.systems.length > 0 && (
								<div className={style.systemsSection}>
									<h4 className={style.systemsTitle}>
										Systems
										{preview.already_migrated && preview.template_uuid && (
											<span className={style.stampStatus}>
												({stampedCount} stamped, {unstampedCount} unstamped
												{skippedCount > 0 ? `, ${skippedCount} skipped` : ''})
											</span>
										)}
										{!preview.already_migrated && skippedCount > 0 && (
											<span className={style.stampStatus}>
												({skippedCount} will be skipped)
											</span>
										)}
									</h4>
									<div className={style.systemsList}>
										{[...preview.systems]
											.sort((a, b) => Number(!!a.is_skipped) - Number(!!b.is_skipped))
											.map((system, idx) => (
											<div
												key={system.uuid || `skipped-${idx}`}
												className={`${style.systemItem} ${
													system.is_skipped ? style.skipped
													: preview.already_migrated
														? (system.is_stamped ? style.stamped : style.unstamped)
														: ''
												}`}
											>
												{/* DEV-11912: quick link to the system's legacy 4.X page on
												    Pinto. Custom-style properties live under their own asp
												    folder (…/asp/31504/Common/1/MainMenu.asp?PropNum=31504&D=1);
												    with custom style OFF the property runs the stock template
												    pages, same path for every system, only D differs
												    (…/TS41/C/MainMenu.asp?PropNum=30001&D=1). */}
												{!system.is_skipped && system.directory_number > 0 && (preview.custom_style || preview.template) && (
													<a
														className={style.pintoButton}
														href={preview.custom_style
															? `https://pinto.touchsource.com/asp/${preview.property_number}/Common/${system.directory_number}/MainMenu.asp?PropNum=${preview.property_number}&D=${system.directory_number}`
															: `https://pinto.touchsource.com/TS41/${preview.template}/MainMenu.asp?PropNum=${preview.property_number}&D=${system.directory_number}`}
														target="_blank"
														rel="noopener noreferrer"
														title={`Open the legacy 4.X page for System ${system.directory_number}`}
													>
														4.X
													</a>
												)}
												{/* Names are "Description - SystemName"
												    (migratedSystemDisplayName) on both Verify steps, so the old
												    bold "{prop}-{dir}" identifier span added nothing and was
												    dropped. (DEV-12065) */}
												<span className={style.systemName}>{system.display_name}</span>
												{/* DEV-11907: each system stamps with ITS design — show it,
												    and let the user change it from the account's templates.
												    "No Design" ('' value) is the default for systems migrated
												    without a design; such systems cannot stamp until a real
												    design is picked here. (DEV-11982) */}
												{preview.already_migrated && !system.is_skipped && system.uuid && (
													system.is_stamped ? (
														system.assigned_design && (
															<span className={style.systemDesignLabel}>{system.assigned_design}</span>
														)
													) : (
														<select
															className={style.systemDesignSelect}
															value={systemStampSelection(system)}
															onChange={(e) => {
																const val = e.target.value;
																setSystemDesigns((prev) => ({ ...prev, [system.uuid]: val }));
															}}
															disabled={stamping || loading}
														>
															<option value="">No Design</option>
															{(preview.account_templates || []).map((t) => (
																<option key={t.uuid} value={t.uuid}>{t.display_name}</option>
															))}
														</select>
													)
												)}
												<span className={style.systemStatus}>
													{system.is_skipped
														? `Will not be migrated — ${system.skip_reason}`
														: preview.already_migrated
															? (system.is_stamped ? 'Stamped' : 'Not Stamped')
															: 'Will be migrated'}
												</span>
												{!system.is_skipped && stampingSystem === system.uuid && (
													<span className={style.stampingIndicator}>Stamping...</span>
												)}
											</div>
										))}
									</div>

									{preview.already_migrated && unstampedCount > 0 && (() => {
										// Only systems with a real design selected stamp; systems on
										// "No Design" are skipped and stay Not Stamped. (DEV-11982)
										const stampableCount = (preview.systems || []).filter(s =>
											!s.is_stamped && !s.is_skipped && systemStampSelection(s)).length;
										return (
											<>
												<button
													className={style.stampButton}
													onClick={handleStampSystems}
													disabled={stamping || loading || stampableCount === 0}
												>
													{stamping
														? `Stamping ${(preview.systems || []).find((s) => s.uuid === stampingSystem)?.display_name || ''}...`
														: stampableCount === 0
															? 'Select a design to stamp'
															: `Stamp ${stampableCount} System${stampableCount > 1 ? 's' : ''}`
													}
												</button>
												{stampableCount < unstampedCount && (
													<div className={style.infoNote}>
														{stampableCount === 0
															? 'All unstamped systems are set to "No Design" — pick a design from a system’s dropdown to stamp it.'
															: `${unstampedCount - stampableCount} system${unstampedCount - stampableCount > 1 ? 's' : ''} set to "No Design" will be skipped and stay Not Stamped.`}
													</div>
												)}
											</>
										);
									})()}

									{/* DEV-11982: broken pre-DEV-11880 leftovers get an actionable
									    warning (their template exists but can't stamp). */}
									{preview.already_migrated && !preview.template_uuid && preview.template_unstampable && (
										<div className={style.warning}>
											<strong>Warning:</strong> This property&apos;s template is missing required design
											data. Pick a design from each system&apos;s dropdown, or re-migrate with a design.
										</div>
									)}
								</div>
							)}

							{preview.already_migrated && !preview.systems?.length && (
								<div className={style.warning}>
									<strong>Warning:</strong> This property has already been migrated but no systems were found.
								</div>
							)}
						</div>
					)}

					{preview && preview.success && !preview.already_migrated
						&& (preview.config_groups || []).some(g => !g.is_main && g.directories?.length) && (
						<div className={style.formGroup}>
							<label className={style.altSectionLabel}>ALTERNATE DESIGN DETECTED</label>
							<div className={style.altCardsRow}>
								{(preview.config_groups || []).filter(g => !g.is_main && g.directories?.length).map((g) => {
									const groupKey = String(g.directories[0]);
									const chosen = altDesigns[groupKey];
									return (
										<div key={groupKey} className={style.altConfigBlock}>
											<h4 className={style.altConfigTitle}>
												<span className={style.altConfigBadge}>{g.directories[0]}</span>
												System{g.directories.length > 1 ? 's' : ''} {g.directories.join(', ')}
											</h4>
											<p><strong>Template:</strong> {g.template || 'N/A'}</p>
											<p><strong>Resolution:</strong> {formatResolution(g.resolution)}</p>
											<p><strong>Static:</strong> {g.static === '1' ? 'Yes' : 'No'}</p>
											{!g.spark_design && !chosen && (
												<div className={style.designNoMatchNote}>
													We do not have a match for this configuration. Please choose the design you want.
												</div>
											)}
											<p className={style.designRow}>
												<strong>Spark Design:</strong>{' '}
												{chosen === NO_DESIGN
													? <span className={style.designNoDesign}>No Design (data import only)</span>
													: chosen
														? <span>{chosen}</span>
														: <span className={style.designMissing}>None selected</span>}
												<button
													className={style.designChangeButton}
													onClick={() => openDesignModal(groupKey)}
													disabled={loading || verifying || stamping}
												>
													Change
												</button>
											</p>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{preview && preview.success && !preview.already_migrated && (() => {
						// DEV-11907: every configuration (main + alternates) needs a
						// choice — a real design or NO DESIGN, independently. (DEV-11982)
						const missingAltDesign = (preview.config_groups || []).some((g) =>
							!g.is_main && g.directories?.length && !altDesigns[String(g.directories[0])]);
						return (
							<>
								<button
									className={style.button}
									data-test-id="migrationMigrateButton"
									onClick={handleMigrate}
									disabled={loading || !accountUUID || !selectedDesign || missingAltDesign}
								>
									{loading ? 'Migrating...' : 'Migrate Property'}
								</button>
								{(!selectedDesign || missingAltDesign) && (
									<div className={style.designRequiredNote}>
										A Spark Design must be chosen for every configuration before this property can be migrated.
									</div>
								)}
							</>
						);
					})()}

					{result && (
						<button
							className={style.resetButton}
							onClick={handleReset}
						>
							Migrate Another Property
						</button>
					)}
				</div>

				{error && (
					<div className={style.error}>
						<strong>Error:</strong> {error}
					</div>
				)}

				{result && (
					<div className={result.success ? style.success : style.error} data-test-id="migrationResult">
						<h3>{result.success ? 'Migration Successful' : 'Migration Failed'}</h3>
						{result.success && migratedPropertyName && (
							<p className={style.propertyName}>{migratedPropertyName}</p>
						)}
						<p>{result.message}</p>
						{result.success && (
							<div className={style.resultDetails}>
								<p><strong>System Group UUID:</strong> {result.system_group_uuid}</p>
								<p><strong>Systems Created:</strong> {result.systems_created}</p>
								<p><strong>Companies Created:</strong> {result.listings_created}</p>
								<p><strong>Individuals Created:</strong> {result.individuals_created}</p>
								<p><strong>Files Migrated:</strong> {result.files_migrated || 0}</p>
								<p data-test-id="migrationResultSlidesCreated"><strong>Slideshow Slides Created:</strong> {result.slides_created || 0}</p>
								{/* DEV-12122: legacy slides that are expired (or carry an
								    unparseable expire date) are not imported; list them so the
								    admin can reconcile against the old Pelican/Atlas slideshow. */}
								{/* DEV-12122: legacy files the migration could not fetch (stale Atlas
								    rows that 404 on updates.touchsource.com, bucket objects gone) or
								    upload — previously only in the bronco log. */}
								{(result.missing_files || []).length > 0 && (
									<div className={style.skippedFiles} data-test-id="migrationMissingFiles">
									<p><strong>{result.missing_files.length} file{result.missing_files.length === 1 ? '' : 's'} could not be migrated:</strong></p>
										<ul>
											{result.missing_files.map((f) => (
												<li key={f}>{f}</li>
											))}
										</ul>
									</div>
								)}
								{(result.skipped_slides || []).length > 0 && (
									<div className={style.skippedFiles} data-test-id="migrationSkippedSlides">
										<p><strong>{result.skipped_slides.length} slide{result.skipped_slides.length === 1 ? '' : 's'} skipped (expired or unreadable):</strong></p>
										<ul>
											{result.skipped_slides.map((f) => (
												<li key={f}>{f}</li>
											))}
										</ul>
									</div>
								)}
								{/* DEV-12016: listing files with unsupported types (only
								    JPG/PNG/GIF/PDF are migrated) are skipped server-side and
								    reported here so the admin knows what didn't come over. */}
								{(result.skipped_files || []).length > 0 && (
									<div className={style.skippedFiles}>
										<p><strong>{result.skipped_files.length} file{result.skipped_files.length === 1 ? '' : 's'} skipped (unsupported type):</strong></p>
										<ul>
											{result.skipped_files.map((f) => (
												<li key={f}>{f}</li>
											))}
										</ul>
									</div>
								)}
								{/* DEV-12124: wayfinding path-network notes (bridged islands,
								    dropped stray points, unsettled passes) so the admin can judge
								    the import and retune the distances. */}
								{(result.wayfinding_warnings || []).length > 0 && (
									<div className={style.skippedFiles} data-test-id="migration-wf-warnings">
										<p><strong>Wayfinding import notes:</strong></p>
										<ul>
											{result.wayfinding_warnings.map((w, i) => (
												<li key={i}>{w}</li>
											))}
										</ul>
									</div>
								)}
								<p className={style.stampReminder}>
									<strong>Note:</strong> Systems have been created but not stamped.
									{' Click "Prepare to Stamp" to see the systems and stamp each with its design — systems migrated with "No Design" stay unstamped until you pick a design for them there.'}
								</p>
							</div>
						)}
					</div>
				)}
			</div>

			{designModalOpen && (
				<div
					className={style.connectivityModalOverlay}
					onClick={closeDesignModal}
				>
					<div
						className={style.designModal}
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-modal="true"
						aria-labelledby="design-modal-title"
					>
						<h3
							id="design-modal-title"
							className={style.designModalTitle}
						>
							Choose Spark Design
						</h3>
						<p className={style.designModalSubtitle}>
							Select the Spark design {preview?.property_name || 'this property'} will migrate into.
						</p>
						{designsError ? (
							<p className={style.connectivityModalBody}>{designsError}</p>
						) : designs && designs.length === 0 ? (
							<p className={style.connectivityModalBody}>
								No designs are available in the Migration Design Portfolio.
							</p>
						) : (
							<div className={style.designSelectGroup}>
								<label className={style.designSelectLabel}>Spark Design</label>
								<div className={style.designSelectRow}>
									<select
										className={style.designSelect}
										value={modalDesign}
										onChange={(e) => {
											setModalDesign(e.target.value);
											clearDesignPreview();
										}}
									>
										<option value="" disabled>Select a design…</option>
										{/* DEV-11982: data-only migration for THIS configuration —
										    no template, no design/listing assignment. Offered for
										    the main design and every alternate group; each
										    configuration chooses independently. */}
										<option value={NO_DESIGN}>
											NO DESIGN — import data only
										</option>
										{(designs || []).map((designName) => (
											<option key={designName} value={designName}>
												{designName}
											</option>
										))}
									</select>
									<button
										className={style.designEyeButton}
										onClick={handleTogglePreview}
										disabled={!modalDesign || modalDesign === NO_DESIGN || designPreviewLoading}
										title="Preview this design"
										aria-label="Preview this design"
									>
										<CustomIcon icon='eye' />
									</button>
								</div>
								{modalDesign === NO_DESIGN && (
									<div className={style.noDesignHint}>
										Imports listings and data without a design — no listing
										groups, collections, scrollers, or buttons are created.
										Best for accounts that already have set designs.
									</div>
								)}
							</div>
						)}

						{(designPreviewLoading || designPreviewError || designPreview) && (
							<div className={style.designPreviewPanel}>
								<div className={style.designPreviewHeader}>
									DESIGN PREVIEW — {(designPreview?.name || modalDesign || '').toUpperCase()}
								</div>
								{designPreviewLoading && (
									<div className={style.designPreviewStatus}>Loading preview…</div>
								)}
								{designPreviewError && (
									<div className={style.designPreviewStatus}>{designPreviewError}</div>
								)}
								{designPreview && (() => {
									const previewHost = window._getEnv('PREVIEW_URL');
									// Orientation-agnostic designs stage both orientations —
									// shrink each so the pair fits side by side.
									const dual = designPreview.previews.length > 1;
									const maxW = dual ? 380 : 660;
									const maxH = dual ? 380 : 420;
									return (
										<div className={style.designPreviewRow}>
											{designPreview.previews.map((p) => {
												const scale = Math.min(maxW / p.width, maxH / p.height);
												const scaledW = Math.round(p.width * scale);
												const scaledH = Math.round(p.height * scale);
												const caption = p.orientation === 'VT'
													? 'Vertical · 9:16'
													: 'Horizontal · 16:9';
												return (
													<div key={p.orientation} className={style.designPreviewItem}>
														<div
															className={style.designPreviewFrameWrap}
															style={{ width: scaledW, height: scaledH }}
														>
															<iframe
																src={`${previewHost}/?store_key=${p.key}`}
																width={p.width}
																height={p.height}
																style={{
																	transform:       `scale(${scale})`,
																	transformOrigin: '0 0',
																	border:          0,
																}}
																title={`Design preview — ${designPreview.name} (${caption})`}
															/>
														</div>
														<div className={style.designPreviewCaption}>{caption}</div>
													</div>
												);
											})}
										</div>
									);
								})()}
							</div>
						)}

						<div className={style.designModalActions}>
							<button
								className={style.designCancelButton}
								onClick={closeDesignModal}
							>
								Cancel
							</button>
							<button
								className={style.designConfirmButton}
								onClick={confirmDesign}
								disabled={!modalDesign}
							>
								Change Design
							</button>
						</div>
					</div>
				</div>
			)}

			{connectivityError && (
				<div
					className={style.connectivityModalOverlay}
					onClick={() => {
						setConnectivityError(null);
					}}
				>
					<div
						className={style.connectivityModal}
						onClick={(e) => {
							e.stopPropagation();
						}}
						role="dialog"
						aria-modal="true"
						aria-labelledby="connectivity-modal-title"
						aria-describedby="connectivity-modal-body"
					>
						<h3
							id="connectivity-modal-title"
							className={style.connectivityModalTitle}
						>
							Migration Unavailable
						</h3>
						<p
							id="connectivity-modal-body"
							className={style.connectivityModalBody}
						>
							{connectivityError.message}
						</p>
						{connectivityError.details && (
							<p className={style.connectivityModalBody}>
								{connectivityError.details}
							</p>
						)}
						<div className={style.connectivityModalActions}>
							<button
								className={style.connectivityModalButton}
								onClick={() => {
									setConnectivityError(null);
								}}
							>
								OK
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default MigrationView;
